import { AgentInstallActionEnum } from "types/muggleTest/agentInstall/AgentInstallActionEnum";
import { AgentInstallTargetEnum } from "types/muggleTest/agentInstall/AgentInstallTargetEnum";
import { IAgentInstallOption } from "types/muggleTest/agentInstall/IAgentInstallOption";

/** GitHub `owner/repo` of the Muggle Works plugin marketplace. */
export const MUGGLE_WORKS_MARKETPLACE_SOURCE = "multiplex-ai/muggle-ai-works";

/** Plugin name inside the Claude Code marketplace. */
export const MUGGLE_WORKS_CLAUDE_PLUGIN_NAME = "muggleai";

/** Name the Muggle MCP server is registered under in an agent's MCP config. */
export const MUGGLE_MCP_SERVER_NAME = "muggle";

/**
 * MCP server entry for agents that take a raw stdio config (Cursor, Antigravity).
 * Runs through npx so the user needs no global install first.
 */
export const MUGGLE_MCP_SERVER_CONFIG = {
  command: "npx",
  args: ["-y", "@muggleai/works", "serve"],
  env: { MUGGLE_MCP_PROMPT_SERVICE_TARGET: "production" },
} as const;

/**
 * Slash command prefilled in Claude Desktop's Code composer. `--marketplace` adds the marketplace
 * and installs in one step; Claude Code confirms the source before adding it.
 */
export const CLAUDE_CODE_INSTALL_COMMAND = `/plugin install ${MUGGLE_WORKS_CLAUDE_PLUGIN_NAME} --marketplace ${MUGGLE_WORKS_MARKETPLACE_SOURCE}`;

/** Claude Desktop link that opens a new Code session with the install command prefilled (not sent). */
export const CLAUDE_CODE_DESKTOP_INSTALL_LINK = `claude://code/new?q=${encodeURIComponent(CLAUDE_CODE_INSTALL_COMMAND)}`;

/** Cursor's MCP install link: Cursor prompts the user to confirm adding the server. */
export const CURSOR_MCP_INSTALL_LINK = `cursor://anysphere.cursor-deeplink/mcp/install?name=${MUGGLE_MCP_SERVER_NAME}&config=${encodeURIComponent(
  btoa(JSON.stringify(MUGGLE_MCP_SERVER_CONFIG))
)}`;

/** Codex command that registers the Muggle Works marketplace. */
export const CODEX_MARKETPLACE_ADD_COMMAND = `codex plugin marketplace add ${MUGGLE_WORKS_MARKETPLACE_SOURCE}`;

/** Codex command that installs the Muggle plugin from that marketplace. */
export const CODEX_PLUGIN_ADD_COMMAND = "codex plugin add muggle@muggle-works";

/** Codex has no install link; both plugin commands chained so a single paste runs them. */
export const CODEX_INSTALL_COMMAND = `${CODEX_MARKETPLACE_ADD_COMMAND} && ${CODEX_PLUGIN_ADD_COMMAND}`;

/** Antigravity has no install link; the `mcpServers` block the user pastes into its raw MCP config. */
export const ANTIGRAVITY_MCP_CONFIG_JSON = JSON.stringify(
  { mcpServers: { [MUGGLE_MCP_SERVER_NAME]: MUGGLE_MCP_SERVER_CONFIG } },
  null,
  2
);

/** How long a copy button reads "Copied" before reverting. */
export const AGENT_INSTALL_COPIED_RESET_MS = 2000;

/** The buttons of the one-click install row, in display order. */
export const AGENT_INSTALL_OPTIONS: IAgentInstallOption[] = [
  {
    target: AgentInstallTargetEnum.CLAUDE_CODE_DESKTOP,
    label: "Claude Code",
    action: AgentInstallActionEnum.OPEN_INSTALL_LINK,
    installValue: CLAUDE_CODE_DESKTOP_INSTALL_LINK,
    afterClickHint:
      "Claude Desktop opens a Code session with the install command filled in. Press Enter and confirm the marketplace. Nothing opened? Install or update Claude Desktop.",
  },
  {
    target: AgentInstallTargetEnum.CURSOR,
    label: "Cursor",
    action: AgentInstallActionEnum.OPEN_INSTALL_LINK,
    installValue: CURSOR_MCP_INSTALL_LINK,
    afterClickHint: "Cursor asks you to confirm adding the Muggle MCP server. Click Install.",
  },
  {
    target: AgentInstallTargetEnum.CODEX,
    label: "Codex",
    action: AgentInstallActionEnum.COPY_INSTALL_TEXT,
    installValue: CODEX_INSTALL_COMMAND,
    afterClickHint: "Install command copied. Paste it in a terminal, then restart Codex and trust the plugin's hooks.",
  },
  {
    target: AgentInstallTargetEnum.ANTIGRAVITY,
    label: "Antigravity",
    action: AgentInstallActionEnum.COPY_INSTALL_TEXT,
    installValue: ANTIGRAVITY_MCP_CONFIG_JSON,
    afterClickHint:
      "MCP config copied. In Antigravity's Agent panel open … → MCP Servers → Manage MCP Servers → View raw config, paste it in, and save.",
  },
];
